<template>
  <ActionPanelShell
    :title="panelTitle"
    :subtitle="subtitle"
    :show-back="interactive && step === 'category'"
    @close="onClose"
    @back="replacePhoto"
  >
    <div class="sp" data-test="photo-panel">
      <!-- The two file pickers live outside every step, so the one that is open survives a re-render.
           Camera: capture="environment" jumps straight to the rear camera (right for "Take photo", wrong
           for the library, where it can hide it on some Android builds) — hence two inputs, as in
           PhotoActionSheet. -->
      <input ref="cameraInput" type="file" accept="image/*" capture="environment" class="pp-hidden" data-test="photo-to-od-camera-input" @change="onPicked">
      <input ref="fileInput" type="file" accept="image/*" class="pp-hidden" data-test="photo-to-od-file-input" @change="onPicked">

      <section v-if="!interactive" class="sp-sec" data-test="photo-unavailable">
        <PanelNotice tone="warn">{{ unavailableText }}</PanelNotice>
      </section>

      <!-- Step 1 — choose a photo. Phones and tablets lead with Take photo; a desktop only has Choose file. -->
      <section v-else-if="step === 'source'" class="sp-sec" data-test="photo-source">
        <div
          class="pp-drop"
          :class="{ 'is-over': dragOver }"
          data-test="photo-drop"
          @dragover.prevent="dragOver = true"
          @dragleave="dragOver = false"
          @drop.prevent="onDrop"
        >
          <span class="pp-drop__ic" aria-hidden="true"><i class="fa fa-picture-o"></i></span>
          <div class="pp-drop__t">{{ isTouch ? 'Add a photo' : 'Drop a photo here' }}</div>
          <div class="pp-drop__d">An insurance card, ID, medication list or referral. JPG, PNG or HEIC.</div>
          <div class="pp-drop__btns">
            <button v-if="isTouch" type="button" class="sp-btn-primary" data-test="photo-to-od-take" @click="takePhoto">
              <i class="fa fa-camera" aria-hidden="true"></i> Take photo
            </button>
            <button
              type="button"
              :class="isTouch ? 'sp-btn-secondary' : 'sp-btn-primary'"
              data-test="photo-to-od-upload"
              @click="choosePhoto"
            >
              <i class="fa fa-upload" aria-hidden="true"></i> {{ isTouch ? 'Choose from library' : 'Choose file' }}
            </button>
          </div>
          <div v-if="!isTouch" class="pp-drop__hint">On a phone or tablet you can take the photo right here.</div>
        </div>
        <p class="pp-lock">
          <i class="fa fa-lock" aria-hidden="true"></i>
          <span>Goes straight into {{ firstName }}’s images in Open Dental. Teamio doesn’t keep a copy.</span>
        </p>
      </section>

      <!-- Step 2 — which Open Dental category? A tap only SELECTS; the footer button is the confirmation. -->
      <template v-else-if="step === 'category'">
        <section class="sp-sec pp-file" data-test="photo-file">
          <div class="pp-thumb">
            <img
              v-if="previewUrl && !previewFailed"
              :src="previewUrl"
              class="pp-thumb__img"
              alt="Photo to send"
              data-test="photo-to-od-preview"
              @error="previewFailed = true"
            >
            <i v-else class="fa fa-picture-o" aria-hidden="true"></i>
          </div>
          <div class="pp-file__text">
            <div class="pp-file__name" data-test="photo-file-name">{{ fileName }}</div>
            <div class="pp-file__meta">{{ fileMeta }}</div>
            <button type="button" class="sp-link" :disabled="sending" data-test="photo-to-od-replace" @click="replacePhoto">Replace photo</button>
          </div>
        </section>

        <section class="sp-sec" data-test="photo-categories">
          <div>
            <div class="sp-h">File it under</div>
            <p class="sp-sub">Pick the Open Dental image category. Nothing is picked for you, so a stray tap can’t misfile it.</p>
          </div>

          <p v-if="categoriesState === 'loading' || categoriesState === 'idle'" class="sp-note" data-test="photo-to-od-categories-loading">
            <i class="fa fa-spinner fa-spin" aria-hidden="true"></i> Loading Open Dental categories…
          </p>

          <template v-else-if="categoriesState === 'error'">
            <PanelNotice tone="error" data-test="photo-to-od-categories-error">Couldn’t load Open Dental categories.</PanelNotice>
            <div>
              <button type="button" class="sp-btn-secondary" data-test="photo-to-od-categories-retry" @click="loadCategories(true)">
                <i class="fa fa-refresh" aria-hidden="true"></i> Retry
              </button>
            </div>
          </template>

          <p v-else-if="!orderedCategories.length" class="sp-note" data-test="photo-to-od-categories-empty">No image categories found in Open Dental.</p>

          <TemplatePicker
            v-else
            :items="categoryItems"
            :selected="selectedIds"
            :searchable="false"
            aria-label="Open Dental category"
            @toggle="selectCategory"
          />
        </section>
      </template>

      <!-- Step 3 — sent. "Take another" keeps the category; the next photo still needs its own Send. -->
      <section v-else class="sp-sec pp-sent" data-test="photo-to-od-sent">
        <span class="pp-sent__ic" aria-hidden="true"><i class="fa fa-check"></i></span>
        <div class="pp-sent__t">Filed in Open Dental</div>
        <div class="pp-sent__d">The photo is in {{ firstName }}’s images under <b>{{ lastSentName }}</b>.</div>
        <div class="pp-sent__n">Back of the card too? Take another and it starts on the same category. It still needs its own Send.</div>
      </section>
    </div>

    <template v-if="interactive && step !== 'source'" #footer>
      <template v-if="step === 'category'">
        <span class="ap-foot-note" data-test="photo-foot-note">{{ footNote }}</span>
        <button
          type="button"
          class="pp-foot-btn ap-foot-main"
          :disabled="!canSend"
          data-test="photo-to-od-send"
          @click="send"
        >{{ sendLabel }}</button>
      </template>
      <template v-else>
        <button type="button" class="pp-foot-secondary" data-test="photo-to-od-take-another" @click="takeAnother">
          <i class="fa fa-camera" aria-hidden="true"></i> Take another
        </button>
        <button type="button" class="pp-foot-btn ap-foot-main" data-test="photo-to-od-done" @click="onClose">Done</button>
      </template>
    </template>
  </ActionPanelShell>
</template>

<script>
/**
 * Photo to Open Dental — the redesigned panel (design "Patient Action Panels" 2h, 3a), opened by
 * PatientPanelActionHost.run('photo') through openActionPanel(… { lockable: true }) (480px, 'ap-slideout').
 *
 * `extends` PatientOdImageUpload: the gate and the demo mask (open() refuses unless `interactive`), the
 * category load, the last-used memory, the SEND (store `patientPhoto/uploadToOd`, the ~2000px JPEG, the
 * toasts, "category no longer exists") and the patient-switch rule are all the classic code. Only the
 * shell around them is new: a panel with a back arrow instead of a Bootstrap sheet.
 *
 * The safety rules are the classic ones, restated by the new copy:
 *   - NOTHING IS PRESELECTED. A tap only selects a category; the last-used one is pinned to the top and
 *     never picked for you; the footer button names the category it will send to — and it IS the
 *     confirmation.
 *   - TAKE PHOTO leads on a phone or tablet (isTouch); a desktop has only Choose file (and drag-and-drop).
 *   - The panel is bound to its patient: a patient change closes it and discards the photo.
 *
 * Closing: PatientOdImageUpload drives its sheet with `sheetOpen`; here `sheetOpen` going false means "close
 * the panel" (one emit of `closePanel`). While a photo is sending, the X is ignored and the host's
 * setCloseLocked(true) also stops a scrim click / Escape — the classic sheet was `locked` the same way.
 *
 * The classic component has NO mixin.js, so its `siteId` / `patNum` / `enabled` props are inherited as
 * they are; the one prop added is `patientName` (for the header and the copy).
 */
import PatientOdImageUpload from '@/pages/PatientProfile/components/PatientOdImageUpload.vue';
import ActionPanelShell from './ActionPanelShell.vue';
import PanelNotice from './PanelNotice.vue';
import TemplatePicker from './TemplatePicker.vue';

export default {
  name: 'PhotoPanel',

  extends: PatientOdImageUpload,

  components: { ActionPanelShell, PanelNotice, TemplatePicker },

  props: {
    /** "First Last" for the header line and the copy. */
    patientName: { type: String, default: '' },
    /** From the host (lockable): lock the slide-out's scrim / Escape while a photo is sending. */
    setCloseLocked: { type: Function, default: null },
  },

  data() {
    return { dragOver: false, previewFailed: false, dismissed: false };
  },

  computed: {
    firstName() {
      return (this.patientName || '').split(/\s+/)[0] || 'the patient';
    },
    subtitle() {
      return this.patientName ? `For ${this.patientName}` : '';
    },
    panelTitle() {
      if (this.step === 'category') return 'File in Open Dental';
      if (this.step === 'sent') return 'Sent to Open Dental';
      return 'Photo to Open Dental';
    },
    unavailableText() {
      return this.masked
        ? 'Photo to Open Dental is off while the demo mask is on.'
        : 'Photo to Open Dental isn’t turned on for this office.';
    },

    fileName() {
      return (this.file && this.file.name) || 'Photo';
    },
    fileMeta() {
      const b = this.file && this.file.size;
      if (!b) return '';
      return b < 1048576 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1048576).toFixed(1)} MB`;
    },

    // OD's own order, the last-used category pinned first — never selected (orderedCategories).
    categoryItems() {
      return this.orderedCategories.map(c => ({
        id: c.DefNum,
        name: c.ItemName,
        badge: this.isLastUsed(c) ? 'Last used' : '',
        badgeTone: 'slate',
      }));
    },
    selectedIds() {
      return this.selectedDefNum === null ? [] : [this.selectedDefNum];
    },
    footNote() {
      return this.sending ? 'Sending to Open Dental…' : '';
    },
  },

  watch: {
    // The classic sheet closes by flipping sheetOpen; the panel closes with it, once.
    sheetOpen(open) {
      if (!open) this.dismiss();
    },
    sending(now) {
      if (typeof this.setCloseLocked === 'function') this.setCloseLocked(!!now);
    },
    step() {
      this.previewFailed = false;
    },
  },

  mounted() {
    // A panel is "the sheet, opened": open() applies the gate and the mask, resets, and reads the last-used category.
    this.open();
  },

  methods: {
    dismiss() {
      if (this.dismissed) return;
      this.dismissed = true;
      this.$emit('closePanel', {});
    },

    /** The X, the phone strip and Done. Ignored mid-send, like the classic sheet's locked close. */
    onClose() {
      if (this.sending) return;
      this.close();
      this.dismiss();
    },

    takePhoto() {
      if (this.$refs.cameraInput) this.$refs.cameraInput.click();
    },

    choosePhoto() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },

    onPicked(e) {
      const input = e.target;
      const file = input.files && input.files[0];
      // Without this, picking the SAME file twice in a row fires no change event.
      try { input.value = ''; } catch (err) { /* some browsers refuse; harmless */ }
      if (file) this.onFileChosen(file);
    },

    onDrop(e) {
      this.dragOver = false;
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (!file) return;
      const isImage = /^image\//.test(file.type) || /\.(heic|heif)$/i.test(file.name || '');
      if (!isImage) {
        if (this.$toast) this.$toast.error('Choose a photo (JPG, PNG or HEIC).');
        return;
      }
      this.onFileChosen(file);
    },

    /** Back arrow / "Replace photo": drop this photo and choose another. The category stays picked. */
    replacePhoto() {
      if (this.sending) return;
      this.revokePreview();
      this.file = null;
      this.capturedPatNum = null;
      this.step = 'source';
    },
  },
};
</script>

<style lang="scss" scoped>
@import './send-panel';

@include send-panel-base;

.pp-hidden { display: none; }

.pp-foot-btn { @include ap-btn-primary; }
.pp-foot-secondary { @include ap-btn-secondary; }

// Step 1 — the drop area.
.pp-drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 40px 24px;
  border: 1.5px dashed $neutral-300;
  border-radius: 16px;
  background: $pp-bg-2;
  text-align: center;

  &.is-over { border-color: $pp-brand-deep; background: $pp-brand-soft; }

  @include ap-phone { padding: 32px 16px; }
}

.pp-drop__ic {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: $pp-brand-soft;
  color: $pp-brand-deep;
  font-size: 22px;
}

.pp-drop__t { margin-top: 4px; font-size: 16px; font-weight: 800; }
.pp-drop__d { max-width: 300px; font-size: 13px; line-height: 1.45; color: $pp-ink-3; }
.pp-drop__hint { font-size: 12px; color: $pp-ink-4; }

.pp-drop__btns {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
}

.pp-lock {
  display: flex;
  gap: 10px;
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: $pp-ink-2;

  i { flex: none; margin-top: 2px; color: $pp-ink-4; }
}

// Step 2 — the chosen photo.
.pp-file {
  flex-direction: row;
  align-items: center;
  gap: 14px;
}

.pp-thumb {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 112px;
  height: 76px;
  overflow: hidden;
  border: 1px solid $pp-line;
  border-radius: 10px;
  background: $pp-bg;
  color: $pp-ink-4;
  font-size: 22px;
}

.pp-thumb__img { display: block; width: 100%; height: 100%; object-fit: contain; }

.pp-file__text { min-width: 0; flex: 1 1 auto; display: flex; flex-direction: column; align-items: flex-start; gap: 2px; }
.pp-file__name { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 14px; font-weight: 700; }
.pp-file__meta { font-size: 12.5px; color: $pp-ink-3; }
.pp-file .sp-link { margin-top: 4px; }

// Step 3 — sent.
.pp-sent {
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 36px;
  text-align: center;
}

.pp-sent__ic {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: $pp-brand-soft;
  color: $teal-600;
  font-size: 26px;
}

.pp-sent__t { margin-top: 4px; font-family: $pp-font; font-size: 22px; font-weight: 800; letter-spacing: -0.02em; }
.pp-sent__d { font-size: 14px; line-height: 1.5; color: $pp-ink-2; }
.pp-sent__n { max-width: 320px; font-size: 13px; line-height: 1.5; color: $pp-ink-3; }
</style>
